import { useState, useCallback, useMemo } from 'react';

export interface UseBulkSelectionOptions<T> {
  items: T[];
  keyExtractor: (item: T) => string | number;
}

export function useBulkSelection<T>({ items, keyExtractor }: UseBulkSelectionOptions<T>) {
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());

  const isSelected = useCallback((item: T) => {
    return selectedIds.has(keyExtractor(item));
  }, [selectedIds, keyExtractor]);

  const toggleSelection = useCallback((item: T) => {
    const id = keyExtractor(item);
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, [keyExtractor]);

  const selectAll = useCallback(() => {
    const allIds = items.map(keyExtractor);
    setSelectedIds(new Set(allIds));
  }, [items, keyExtractor]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const selectedItems = useMemo(() => {
    return items.filter(item => selectedIds.has(keyExtractor(item)));
  }, [items, selectedIds, keyExtractor]);

  const isAllSelected = useMemo(() => {
    return items.length > 0 && items.every(item => selectedIds.has(keyExtractor(item)));
  }, [items, selectedIds, keyExtractor]);

  const isPartiallySelected = useMemo(() => {
    return selectedIds.size > 0 && !isAllSelected;
  }, [selectedIds.size, isAllSelected]);

  const selectedCount = selectedIds.size;

  return {
    selectedIds,
    selectedItems,
    selectedCount,
    isSelected,
    toggleSelection,
    selectAll,
    clearSelection,
    isAllSelected,
    isPartiallySelected,
  };
}